import { cn } from '@repo/tailwind-config/tailwind.functions'
import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { StringFormatter } from '@repo/toolkit/string'
import Typography from '@repo/ui/typography'
import { FieldValues, Path, useFormContext } from 'react-hook-form'

import { FormControl, FormField, FormItem } from '../forms/form'
import ControlledRadioInput from './controlled-radio-input'

type BaseProps<F extends FieldValues, T extends string> = {
  list: T[]
  viewMode?: 'row' | 'column'
  name: Path<F>
  required?: boolean
  mapping?: Record<T, string>
  wrapperClassName?: string
  radioClassName?: string
  disabledRadioInputs?: T[]
  /** Transform the display value. Applied after mapping (if provided). */
  valueFormatter?: (value: string) => string
}

// label hidden → you must NOT pass label
type HiddenLabelProps<F extends FieldValues, T extends string> = BaseProps<F, T> & {
  hideLabel: true
  label?: never
}

// label shown → you MUST pass label
type ShownLabelProps<F extends FieldValues, T extends string> = BaseProps<F, T> & {
  hideLabel?: false | undefined
  label: string
}

export type EnumRadioGroupProps<F extends FieldValues, T extends string> =
  | HiddenLabelProps<F, T>
  | ShownLabelProps<F, T>

/**
 * Renders a group of controlled radio inputs based on list.
 */
export function ControlledListRadioGroup<F extends FieldValues, T extends string>({
  list,
  name,
  label,
  mapping,
  disabledRadioInputs,
  wrapperClassName,
  radioClassName,
  viewMode = 'row',
  required = false,
  valueFormatter
}: EnumRadioGroupProps<F, T>) {
  const { control } = useFormContext<F>()

  return (
    <div
      className={cn(
        'flex gap-3 items-center',
        {
          'flex-col items-start': viewMode === 'column',
          'flex-row': viewMode === 'row'
        },
        wrapperClassName
      )}
    >
      {label && (
        <Typography variant="label-medium" color={tailwindColors.black.DEFAULT}>
          {`${StringFormatter.isRequired(label, required)} : `}
        </Typography>
      )}
      <FormField
        control={control}
        name={name}
        render={({ field }) => (
          <FormItem>
            <FormControl>
              <div
                className={cn(
                  'flex gap-3',
                  {
                    'flex-col': viewMode === 'column',
                    'flex-row': viewMode === 'row'
                  },
                  radioClassName
                )}
              >
                {list.map((value) => (
                  <ControlledRadioInput
                    key={value}
                    id={`${name}-${value}`}
                    checked={field.value === value}
                    labelClassName="text-nowrap font-medium "
                    label={(() => {
                      const displayValue = mapping ? mapping[value] : value
                      return valueFormatter ? valueFormatter(displayValue) : displayValue
                    })()}
                    disabled={disabledRadioInputs?.includes(value)}
                    onCheck={() => {
                      if (disabledRadioInputs?.includes(value)) return
                      field.onChange(value)
                    }}
                  />
                ))}
              </div>
            </FormControl>
          </FormItem>
        )}
      />
    </div>
  )
}
